import React from 'react';
import '../components/grammar-page.css';
import { FaBookOpen, FaLightbulb } from 'react-icons/fa';

const AdverbsOfFrequencyExplanation = () => {
  return (
    <div className="grammar-explanation">
      <h2><FaBookOpen /> Adverbs of Frequency</h2>

      <p>
        We use <strong>adverbs of frequency</strong> with the present simple to say how often we do something.
      </p>

      <div className="grammar-explanation-grid">
        <div className="example-block">
          <h3><FaLightbulb /> Common Adverbs of Frequency (100% → 0%)</h3>
          <ul>
            <li><strong>always</strong> (100%)</li>
            <li><strong>frequently</strong> / <strong>usually</strong> / <strong>generally</strong> / <strong>normally</strong> / <strong>regularly</strong> (~80-90%)</li>
            <li><strong>often</strong> (~60-70%)</li>
            <li><strong>sometimes</strong> / <strong>occasionally</strong> (~30-50%)</li>
            <li><strong>rarely</strong> / <strong>seldom</strong> / <strong>infrequently</strong> / <strong>hardly ever</strong> (~5-15%)</li>
            <li><strong>never</strong> (0%)</li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Position: Middle of Sentence (Standard)</h3>
          <p>Put the adverb between the subject and the main verb.</p>
          <ul>
            <li><em>I <strong>often</strong> go to the cinema.</em></li>
            <li><em>She <strong>sometimes</strong> visits me at home.</em></li>
            <li><em>We <strong>usually</strong> drink coffee.</em></li>
            <li><em>He <strong>never</strong> eats meat.</em></li>
          </ul>
          <p><strong>With <em>be</em>:</strong> Put the adverb after <em>be</em>.</p>
          <ul>
            <li><em>She is <strong>always</strong> late.</em></li>
            <li><em>They are <strong>usually</strong> happy.</em></li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Position: Beginning or End (Emphasis)</h3>
          <p>Put the adverb at the start or end for emphasis. This is stronger.</p>
          <ul>
            <li><em><strong>Often</strong> I go to the cinema.</em> (emphasizes frequency)</li>
            <li><em>I go to the cinema <strong>often</strong>.</em></li>
            <li><em><strong>Sometimes</strong> she visits me.</em></li>
            <li><em>She visits me <strong>sometimes</strong>.</em></li>
          </ul>
          <p className="error-note"><strong>❌ Never:</strong> <em>I go often to the cinema.</em> (Don't put adverb between verb and object)</p>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Longer Expressions (Beginning or End Only)</h3>
          <p>These phrases go at the beginning or end of the sentence, NOT in the middle.</p>
          <ul>
            <li><em>once in a while</em> — <em>I go to the cinema <strong>once in a while</strong>.</em></li>
            <li><em>every now and again</em> — <em>She drinks wine <strong>every now and again</strong>.</em></li>
            <li><em>from time to time</em> — <em><strong>From time to time</strong> I visit my mother.</em></li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Number + Times + Period</h3>
          <p>Use a number (or several/many) + <em>times</em> + <em>a</em> + period of time.</p>
          <ul>
            <li><strong>once</strong> (not one time) — <em>I go to the cinema <strong>once a week</strong>.</em></li>
            <li><strong>twice</strong> (not two times) — <em>She takes tablets <strong>twice a day</strong>.</em></li>
            <li><strong>three times</strong> — <em>I change sheets <strong>three times a month</strong>.</em></li>
            <li><strong>several times</strong> — <em>I meet him <strong>several times a year</strong>.</em></li>
            <li><strong>many times</strong> — <em>We visit <strong>many times a year</strong>.</em></li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Every + Period of Time</h3>
          <ul>
            <li><em>every morning / day / week / month / year</em></li>
            <li><em>every Tuesday / Monday / weekend</em></li>
            <li><em>I take a class <strong>every Wednesday</strong>.</em></li>
          </ul>
          <p><strong>Day + s = every day:</strong></p>
          <ul>
            <li><em>I take a dance class <strong>on Wednesdays</strong>.</em> (= every Wednesday)</li>
            <li><em>I relax <strong>on Saturdays</strong>.</em> (= every Saturday)</li>
          </ul>
        </div>

        <div className="grammar-flex-table">
          {[
            ['Adverb', 'Frequency %', 'Example'],
            ['always', '100%', 'I always brush my teeth.'],
            ['usually', '~90%', 'She usually walks to work.'],
            ['often', '~70%', 'We often eat out.'],
            ['sometimes', '~40%', 'He sometimes cooks.'],
            ['occasionally', '~30%', 'They occasionally travel.'],
            ['rarely', '~10%', 'I rarely drink alcohol.'],
            ['never', '0%', 'She never smokes.'],
          ].map((row, index) => (
            <div key={index} className={`grammar-flex-row${index === 0 ? ' header' : ''}`}>
              {row.map((cell, i) => (
                <div key={i} className="grammar-cell">{cell}</div>
              ))}
            </div>
          ))}
        </div>

        <div className="grammar-explanation-text">
          <h3><FaLightbulb /> Summary of Positions</h3>
          <ul>
            <li><strong>Middle (standard):</strong> Subject + adverb + verb — <em>I often go.</em></li>
            <li><strong>After be:</strong> Subject + be + adverb — <em>She is always late.</em></li>
            <li><strong>Beginning (emphasis):</strong> Adverb + subject + verb — <em>Often I go.</em></li>
            <li><strong>End (emphasis):</strong> Subject + verb + adverb — <em>I go often.</em></li>
            <li><strong>Longer phrases:</strong> Only beginning or end — <em>once a week, every day, from time to time</em></li>
          </ul>
          <p><strong>Examples:</strong></p>
          <ul>
            <li>I <strong>always</strong> wake up at 7 a.m.</li>
            <li>She <strong>usually</strong> takes the bus to work.</li>
            <li>We <strong>sometimes</strong> go to the park on Sundays.</li>
            <li>He <strong>rarely</strong> eats fast food.</li>
            <li>They <strong>never</strong> forget their homework.</li>
            <li>I visit my parents <strong>once a month</strong>.</li>
            <li>She goes to the gym <strong>three times a week</strong>.</li>
            <li>We meet <strong>every Friday</strong>.</li>
            <li><strong>From time to time</strong> I see my old friends.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdverbsOfFrequencyExplanation;